import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Typed state stored in the URL search params. `update` replaces the history
 * entry; a patch that returns null leaves the URL unchanged.
 */
export const useSearchParamsState = <T>(
  read: (params: URLSearchParams) => T,
  write: (params: URLSearchParams, value: T) => URLSearchParams,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = useMemo(() => read(searchParams), [read, searchParams]);

  const update = useCallback(
    (patch: (current: T) => Partial<T> | null) =>
      setSearchParams(
        (prev) => {
          const current = read(prev);
          const change = patch(current);
          return change ? write(prev, { ...current, ...change }) : prev;
        },
        { replace: true },
      ),
    [read, write, setSearchParams],
  );

  return [value, update] as const;
};
